import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, Filler, registerables } from 'chart.js';
import { useLang } from '../LanguageContext';
import { useTheme, getClimateChartPalette } from '../context/ThemeContext';
import { useFarm } from '../context/FarmContext';
import {
  formatDeviceTimestamp,
  parseTimestamp,
  formatChartQuarterHourTime,
  formatChartTooltipTime,
  formatChartAxisTimeSuppressDuplicates,
} from '../utils/timeUtils';
import { buildChartAreaGradient } from '../utils/chartAreaGradient';
import './ClimateCharts.css';

const CHART_METRIC_TEMPLATE = [
  { key: 'temperature', titleKey: 'temperature', unit: '°C', paletteKey: 'temperature', valueKey: 'temperature', digits: 1 },
  { key: 'humidity', titleKey: 'humidity', unit: '%', paletteKey: 'humidity', valueKey: 'humidity', digits: 1 },
  { key: 'co2', titleKey: 'co2', unit: 'ppm', paletteKey: 'co2', valueKey: 'co2', digits: 1 },
  { key: 'vpd', titleKey: 'vpd', unit: 'kPa', paletteKey: 'vpd', valueKey: 'vpd', digits: 2 },
];
const rawClimatePts = Number(process.env.REACT_APP_CLIMATE_CHART_MAX_POINTS) || 1100;
const CLIMATE_CHART_MAX_POINTS = Math.min(1200, Math.max(1000, rawClimatePts));
function normalizeLightBinary(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const token = value.trim().toLowerCase();
    if (token === 'day' || token === 'день' || token === 'on' || token === 'true') return 1;
    if (token === 'night' || token === 'ночь' || token === 'off' || token === 'false') return 0;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  // Binary payload support: 1 = day, 0 = night.
  if (numeric === 0 || numeric === 1) return numeric;
  // Lux fallback support: values below 5 are treated as night.
  return numeric < 5 ? 0 : 1;
}

function normalizeDayStateFromStatus(value) {
  const token = String(value || '').trim().toLowerCase();
  if (!token) return null;
  if (['day', 'daylight', 'день', 'on', 'true'].includes(token)) return 1;
  if (['night', 'ночь', 'off', 'false'].includes(token)) return 0;
  return null;
}

function parseHmToMinutes(value, fallbackMinutes) {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return fallbackMinutes;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return fallbackMinutes;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return fallbackMinutes;
  return hours * 60 + minutes;
}

function isMinuteInDayWindow(minute, dayStartMinute, dayEndMinute) {
  if (dayStartMinute === dayEndMinute) return true;
  if (dayStartMinute < dayEndMinute) {
    return minute >= dayStartMinute && minute < dayEndMinute;
  }
  return minute >= dayStartMinute || minute < dayEndMinute;
}

const nightZonePlugin = {
  id: 'nightZone',
  beforeDatasetsDraw(chart, _args, options) {
    const nightMask = options?.nightMask;
    if (!Array.isArray(nightMask) || !nightMask.length) return;
    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    if (!xScale || !chartArea) return;

    const ranges = [];
    let rangeStart = null;
    for (let i = 0; i < nightMask.length; i += 1) {
      if (nightMask[i] && rangeStart == null) {
        rangeStart = i;
      }
      if ((!nightMask[i] || i === nightMask.length - 1) && rangeStart != null) {
        const end = nightMask[i] ? i : i - 1;
        ranges.push([rangeStart, end]);
        rangeStart = null;
      }
    }

    ctx.save();
    ctx.fillStyle = options.color || 'rgba(148, 163, 184, 0.14)';
    ranges.forEach(([startIndex, endIndex]) => {
      const startPixel = xScale.getPixelForValue(startIndex);
      const endPixel = xScale.getPixelForValue(endIndex);
      const prevPixel = startIndex > 0 ? xScale.getPixelForValue(startIndex - 1) : chartArea.left;
      const nextPixel = endIndex < nightMask.length - 1 ? xScale.getPixelForValue(endIndex + 1) : chartArea.right;
      const left = startIndex > 0 ? (prevPixel + startPixel) / 2 : chartArea.left;
      const right = endIndex < nightMask.length - 1 ? (endPixel + nextPixel) / 2 : chartArea.right;
      ctx.fillRect(left, chartArea.top, Math.max(right - left, 0), chartArea.bottom - chartArea.top);

      const zoneWidth = right - left;
      if (zoneWidth > 96) {
        ctx.fillStyle = options.labelColor || 'rgba(255,255,255,0.82)';
        ctx.font = '600 11px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(options.label || '', left + zoneWidth / 2, chartArea.top + 14);
        ctx.fillStyle = options.color || 'rgba(148, 163, 184, 0.14)';
      }
    });
    ctx.restore();
  },
};

ChartJS.register(...registerables, Filler, nightZonePlugin);

function toDisplayName(device) {
  return (
    device?.customName ||
    device?.deviceName ||
    device?.name ||
    null
  );
}

function toDisplayId(device, deviceId) {
  return device?.deviceId || device?.id || deviceId || '--';
}

function toShortDisplayId(value) {
  const raw = String(value || '').trim();
  if (!raw || raw === '--') return '--';
  if (raw.length <= 14) return raw;
  return `${raw.slice(0, 6)}...${raw.slice(-4)}`;
}

async function copyText(value) {
  const text = String(value || '');
  if (!text) return false;

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  if (typeof document === 'undefined') return false;

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();

  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  return copied;
}

function extractNumericMetric(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const match = String(value).replace(',', '.').match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(1)) : null;
}

function formatMetricValue(value, unit, digits = 1) {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';
  const normalized = Number(value);
  const formatted = normalized.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${formatted} ${unit}`.trim();
}

function formatLightState(value, dayLabel, nightLabel) {
  if (value === 1) return `${dayLabel} ☀️`;
  if (value === 0) return `${nightLabel} 🌙`;
  return '--';
}

function normalizeRelayValue(value) {
  const rawValue =
    value && typeof value === 'object' && !Array.isArray(value)
      ? value.state
      : value;
  if (rawValue === null || rawValue === undefined || rawValue === '') return null;
  const numeric = Number(rawValue);
  if (!Number.isFinite(numeric)) return null;
  return numeric === 1;
}

function normalizeRelayMap(source = {}) {
  const relays = source?.relays || source?.meta?.relays || {};
  const socketStates = source?.socketStates || source?.sockets || source?.meta?.sockets || {};
  const deviceStatuses = source?.deviceStatuses || source?.meta?.device_statuses || {};
  const controlOutlets = Array.isArray(source?.control?.outlets) ? source.control.outlets : [];
  const controlOutletState = Object.fromEntries(controlOutlets.map((outlet) => [outlet?.key, outlet?.state]));
  const toRelayBit = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'boolean') return value ? 1 : 0;
    const token = String(value).trim().toLowerCase();
    if (token === 'on' || token === 'active' || token === 'true') return 1;
    if (token === 'off' || token === 'inactive' || token === 'false') return 0;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    return numeric === 1 ? 1 : 0;
  };
  const acRelay = toRelayBit(
    controlOutletState.ac ??
      relays.ac ??
      relays.rA ??
      source?.rA ??
      socketStates.temperature ??
      deviceStatuses.ac
  );
  const dryerRelay = toRelayBit(
    controlOutletState.dryer ??
      relays.dryer ??
      relays.dehumidifier ??
      relays.rD ??
      source?.rD ??
      socketStates.humidity ??
      deviceStatuses.dehumidifier
  );
  const co2Relay = toRelayBit(
    controlOutletState.co2 ??
      relays.co2 ??
      relays.rC ??
      source?.rC ??
      socketStates.co2 ??
      deviceStatuses.co2
  );
  const extraRelay = toRelayBit(
    controlOutletState.extra ??
      relays.extra ??
      relays.reserve ??
      relays.rL ??
      source?.rL ??
      socketStates.reserve ??
      deviceStatuses.reserve
  );
  return {
    ac: acRelay,
    dryer: dryerRelay,
    co2: co2Relay,
    extra: extraRelay,
  };
}

function normalizeRelayModes(source = {}) {
  const modes = source?.relayModes || source?.meta?.relayModes || {};
  const normalize = (value) => {
    const token = String(value || '').trim().toLowerCase();
    return token === 'manual_on' || token === 'manual_off' || token === 'auto' ? token : 'auto';
  };
  return {
    ac: normalize(modes.ac),
    dryer: normalize(modes.dryer),
    co2: normalize(modes.co2),
    extra: normalize(modes.extra),
  };
}

function RelayBadge({ label, value, onLabel, offLabel, stale = false }) {
  const isOn = normalizeRelayValue(value);
  const mode = String(value?.mode || '').toLowerCase();
  const showAutoIndicator = mode === 'auto';
  return (
    <span className={`shefa-socket-chip ${isOn ? 'shefa-socket-chip--on' : 'shefa-socket-chip--off'} ${stale ? 'shefa-socket-chip--stale' : ''}`}>
      <span className="shefa-socket-chip__dot" aria-hidden="true" />
      <span className="shefa-socket-chip__label">{label}</span>
      {showAutoIndicator ? <span className="shefa-socket-chip__auto">A</span> : null}
      <strong className="shefa-socket-chip__state">{isOn ? onLabel : offLabel}</strong>
    </span>
  );
}

function clampBounds(values, fallbackPadding = 1) {
  const valid = values.filter((value) => value !== null && value !== undefined && !Number.isNaN(value));
  if (!valid.length) return { min: 0, max: 1 };
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  if (min === max) {
    return {
      min: min - fallbackPadding,
      max: max + fallbackPadding,
    };
  }
  const padding = Math.max((max - min) * 0.2, fallbackPadding);
  return {
    min: min - padding,
    max: max + padding,
  };
}

function renderAction(label, className, { onClick, href, disabled = false }) {
  if (href) {
    return (
      <Link className={className} to={href}>
        {label}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );
}

function ClimateMetricChart({
  spec,
  points,
  value,
  text,
  palette,
  nightMask,
  isMobileViewport = false,
}) {
  const values = points.map((point) => point[spec.valueKey]);
  const showNightZones = true;
  const bounds = clampBounds(values, spec.key === 'co2' ? 40 : spec.key === 'vpd' ? 0.15 : 1);

  const chartData = {
    labels: points.map((point) => point.axisLabel),
    datasets: [
      {
        data: values,
        borderColor: spec.color,
        backgroundColor: (context) => buildChartAreaGradient(context, spec.color),
        fill: true,
        borderWidth: 2,
        borderJoinStyle: 'round',
        borderCapStyle: 'round',
        cubicInterpolationMode: 'monotone',
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointBorderWidth: 0,
        pointBackgroundColor: 'transparent',
        pointHoverBackgroundColor: spec.color,
        stepped: false,
      },
    ],
  };

  const options = {
    animation: false,
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      line: {
        cubicInterpolationMode: 'monotone',
        tension: 0.4,
        borderJoinStyle: 'round',
        borderCapStyle: 'round',
      },
      point: {
        radius: 0,
        hoverRadius: 4,
        hitRadius: 12,
        borderWidth: 0,
      },
    },
    plugins: {
      legend: { display: false },
      nightZone: {
        nightMask: showNightZones ? nightMask : [],
        color: palette.nightZone,
        label: text.nightPhaseInRoom,
        labelColor: palette.textPrimary || 'rgba(255,255,255,0.82)',
      },
      tooltip: {
        backgroundColor: palette.tooltipBg,
        displayColors: false,
        padding: 10,
        callbacks: {
          title: (items) => {
            const dataIndex = items[0]?.dataIndex;
            if (dataIndex == null) return '';
            return points[dataIndex]?.tooltipLabel || points[dataIndex]?.axisLabel || '';
          },
          label: (ctx) => formatMetricValue(ctx.parsed.y, spec.unit, spec.digits),
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: { display: false },
        ticks: {
          display: true,
          autoSkip: true,
          color: palette.tick,
          font: { size: isMobileViewport ? 10 : 11, weight: 500 },
          maxRotation: 0,
          maxTicksLimit: isMobileViewport ? 4 : 8,
          autoSkipPadding: isMobileViewport ? 14 : 18,
          callback: (_value, index) =>
            formatChartAxisTimeSuppressDuplicates(
              points.map((point) => point.axisLabel),
              index
            ),
        },
      },
      y: {
        min: bounds.min,
        max: bounds.max,
        grid: {
          display: false,
        },
        border: { display: false },
        ticks: {
          // Keep Y scale visible, but round values to remove float noise.
          display: true,
          color: palette.tick,
          font: { size: 11, weight: 500 },
          maxTicksLimit: 4,
          callback: (tickValue) => {
            const numeric = Number(tickValue);
            if (!Number.isFinite(numeric)) return '';
            if (spec.key === 'vpd') return numeric.toFixed(2);
            return String(Math.round(numeric));
          },
        },
      },
    },
  };

  return (
    <div className="climate-chart-block">
      <div className="climate-chart-header">
        <h6 className="climate-chart-title mb-0">
          {spec.title}
          {spec.unit ? ` (${spec.unit})` : ''}
        </h6>
        <div className="climate-chart-value">{formatMetricValue(value, spec.unit, spec.digits)}</div>
      </div>
      <div className="chart-container">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
}

export default function ClimateCharts({
  deviceId,
  device = null,
  latestReading = null,
  onSettings = null,
  settingsHref = '',
  farmPlan: farmPlanProp = null,
}) {
  const { t, lang } = useLang();
  const { isLight } = useTheme();
  const { farmPlan: contextFarmPlan } = useFarm();
  const palette = useMemo(() => getClimateChartPalette(isLight), [isLight]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  const [deviceIdExpanded, setDeviceIdExpanded] = useState(false);
  const [deviceIdCopied, setDeviceIdCopied] = useState(false);

  const resolvedFarmPlan = farmPlanProp ?? device?.farmPlan ?? contextFarmPlan;
  const maxHistoryHoursFromPlan =
    resolvedFarmPlan?.historyLimitDays != null
      ? Math.max(1, Number(resolvedFarmPlan.historyLimitDays) * 24)
      : 24;
  const requestHistoryHours = Math.min(24, maxHistoryHoursFromPlan);
  const vpdAnalysisAllowed =
    !resolvedFarmPlan || resolvedFarmPlan.features?.vpdAnalysis === true;

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    const downsample = requestHistoryHours <= 24 ? 5 : requestHistoryHours <= 48 ? 10 : 15;
    fetch(
      `/api/sensor-data/climate/history/${deviceId}?hours=${requestHistoryHours}&downsampleMinutes=${downsample}&maxPoints=${CLIMATE_CHART_MAX_POINTS}`,
      {
        signal: controller.signal,
      }
    )
      .then((response) => (response.ok ? response.json() : []))
      .then((payload) => (Array.isArray(payload) ? payload : []))
      .then((payload) => {
        if (!active) return;
        setHistory(payload);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.error('Failed to fetch history', error);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [deviceId, requestHistoryHours]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const media = window.matchMedia('(max-width: 767.98px)');
    const applyViewport = (event) => setIsMobileViewport(event.matches);

    setIsMobileViewport(media.matches);
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', applyViewport);
      return () => media.removeEventListener('change', applyViewport);
    }

    media.addListener(applyViewport);
    return () => media.removeListener(applyViewport);
  }, []);

  const text = useMemo(
    () => ({
      settings: t('settings'),
      on: t('statusOn'),
      offline: t('statusOff'),
      updated: t('updated'),
      online: t('online'),
      noData24h: t('climateNoData24h'),
      relays: t('climateRelays'),
      day: t('day'),
      night: t('night'),
      climateLightStatus: t('climateLightStatus'),
      nightPhaseInRoom: t('climateNightPhaseInRoom'),
      relayAc: t('relayLabels.A'),
      relayDryer: t('relayLabels.D'),
      relayCo2: t('relayLabels.C'),
      relayExtra: t('relayLabels.reserve'),
      idPreviewAria: t('deviceCard.idPreviewAria'),
      copied: t('deviceCard.copied'),
    }),
    [t]
  );

  const chartSpecs = useMemo(
    () =>
      CHART_METRIC_TEMPLATE.map((spec) => ({
        ...spec,
        color: palette[spec.paletteKey],
        title: t(spec.titleKey),
      })),
    [t, palette]
  );

  const locale = lang === 'he' ? 'he-IL' : lang === 'es' ? 'es-ES' : lang || 'ru-RU';

  const points = useMemo(
    () =>
      history.map((entry) => {
        return {
          timestamp: entry.timestamp,
          axisLabel: formatChartQuarterHourTime(entry.timestamp, locale),
          tooltipLabel: formatChartTooltipTime(entry.timestamp, locale),
          temperature: extractNumericMetric(entry.temperature),
          humidity: extractNumericMetric(entry.humidity),
          co2: extractNumericMetric(entry.co2),
          light: extractNumericMetric(entry.light),
          lightStatus: entry.lightStatus,
          vpd: extractNumericMetric(entry.vpd),
        };
      }),
    [history, locale]
  );

  const nightMask = useMemo(() => {
    const dayStartRaw =
      device?.dayStart ||
      device?.config?.dayStart ||
      device?.schedule?.dayStart ||
      latestReading?.dayStart ||
      '07:00';
    const dayEndRaw =
      device?.dayEnd ||
      device?.nightStart ||
      device?.config?.dayEnd ||
      device?.config?.nightStart ||
      device?.schedule?.dayEnd ||
      device?.schedule?.nightStart ||
      latestReading?.dayEnd ||
      latestReading?.nightStart ||
      '19:00';
    const dayStartMinute = parseHmToMinutes(dayStartRaw, 7 * 60);
    const dayEndMinute = parseHmToMinutes(dayEndRaw, 19 * 60);

    return points.map((point) => {
      const byLight = normalizeLightBinary(point.light);
      if (byLight != null) return byLight === 0;
      const byStatus = normalizeDayStateFromStatus(point.lightStatus);
      if (byStatus != null) return byStatus === 0;
      const ts = parseTimestamp(point.timestamp);
      if (!ts) return false;
      const minute = ts.getHours() * 60 + ts.getMinutes();
      return !isMinuteInDayWindow(minute, dayStartMinute, dayEndMinute);
    });
  }, [points, device, latestReading]);

  const latestRawPoint = history[history.length - 1] || null;
  const latestPoint = points[points.length - 1] || null;
  const deviceTitle = toDisplayName(device) || t('deviceCard.unknownDeviceName');
  const fullDeviceId = toDisplayId(device, deviceId);
  const shortDeviceId = toShortDisplayId(fullDeviceId);
  const latestTimestampCandidates = [
    ['latestReading.lastRealSensorUpdateAt', latestReading?.lastRealSensorUpdateAt],
    ['latestReading.latestTelemetryAt', latestReading?.latestTelemetryAt],
    ['latestReading.lastTelemetryAt', latestReading?.lastTelemetryAt],
    ['latestReading.latestTelemetry.timestamp', latestReading?.latestTelemetry?.timestamp],
    ['history.latest.timestamp', latestRawPoint?.timestamp || history[history.length - 1]?.timestamp],
    ['device.lastRealSensorUpdateAt', device?.lastRealSensorUpdateAt],
    ['device.latestTelemetryAt', device?.latestTelemetryAt],
    ['device.lastTelemetryAt', device?.lastTelemetryAt],
    ['device.latestTelemetry.timestamp', device?.latestTelemetry?.timestamp],
    ['latestReading.timestamp', latestReading?.timestamp],
    ['device.timestamp', device?.timestamp],
  ];
  const latestTimestampEntry =
    latestTimestampCandidates.find(([, value]) => value !== undefined && value !== null && value !== '') || [];
  const latestTimestampSource = latestTimestampEntry[0] || 'none';
  const latestTimestamp = latestTimestampEntry[1] || null;
  useEffect(() => {
    if (typeof console === 'undefined' || typeof console.debug !== 'function') return;
    console.debug('[UI][FreshnessSource][ClimateCharts]', {
      deviceId: fullDeviceId,
      source: latestTimestampSource,
      timestamp: latestTimestamp,
      deviceStatusLastSeenAt: device?.deviceStatus?.lastSeenAt || device?.lastSeenAt || null,
      deviceStatusUpdatedAt: device?.deviceStatus?.updatedAt || null,
    });
  }, [
    fullDeviceId,
    latestTimestampSource,
    latestTimestamp,
    device?.deviceStatus?.lastSeenAt,
    device?.deviceStatus?.updatedAt,
    device?.lastSeenAt,
  ]);
  const headerTimestamp = latestTimestamp
    ? formatDeviceTimestamp(parseTimestamp(latestTimestamp), locale)
    : '--';
  const lastSeenMs = latestTimestamp ? Date.now() - parseTimestamp(latestTimestamp).getTime() : Number.POSITIVE_INFINITY;
  const isExplicitOffline = String(latestReading?.status || device?.status || '').toLowerCase() === 'offline';
  const connectionState = isExplicitOffline
    ? 'offline'
    : Number.isFinite(lastSeenMs) && lastSeenMs <= 5 * 60 * 1000
      ? 'online'
      : Number.isFinite(lastSeenMs) && lastSeenMs <= 15 * 60 * 1000
        ? 'delayed'
        : 'offline';
  const isStaleConnection = connectionState !== 'online';
  const statusBadgeText =
    connectionState === 'online'
      ? t('deviceCard.connectivityOnline')
      : connectionState === 'delayed'
        ? t('deviceCard.connectivityNoRecentData')
        : t('deviceCard.connectivityOffline');
  const statusBadgeEmoji = connectionState === 'online' ? '🟢' : connectionState === 'delayed' ? '🟡' : '🔴';

  const liveMetrics = {
    temperature:
      extractNumericMetric(latestReading?.parsedReadings?.temperature) ??
      extractNumericMetric(latestReading?.temperature) ??
      extractNumericMetric(device?.parsedReadings?.temperature) ??
      extractNumericMetric(device?.temperature) ??
      extractNumericMetric(latestRawPoint?.temperature) ??
      latestPoint?.temperature ??
      null,
    humidity:
      extractNumericMetric(latestReading?.parsedReadings?.humidity) ??
      extractNumericMetric(latestReading?.humidity) ??
      extractNumericMetric(device?.parsedReadings?.humidity) ??
      extractNumericMetric(device?.humidity) ??
      extractNumericMetric(latestRawPoint?.humidity) ??
      latestPoint?.humidity ??
      null,
    co2:
      extractNumericMetric(latestReading?.parsedReadings?.co2) ??
      extractNumericMetric(latestReading?.co2) ??
      extractNumericMetric(device?.parsedReadings?.co2) ??
      extractNumericMetric(device?.co2) ??
      extractNumericMetric(latestRawPoint?.co2) ??
      latestPoint?.co2 ??
      null,
    light:
      normalizeLightBinary(latestReading?.parsedReadings?.light) ??
      normalizeLightBinary(latestReading?.light) ??
      normalizeLightBinary(device?.parsedReadings?.light) ??
      normalizeLightBinary(device?.light) ??
      normalizeLightBinary(latestRawPoint?.light) ??
      normalizeLightBinary(latestPoint?.light) ??
      null,
    vpd: vpdAnalysisAllowed
      ? extractNumericMetric(latestReading?.parsedReadings?.vpd) ??
        extractNumericMetric(latestReading?.vpd) ??
        extractNumericMetric(device?.parsedReadings?.vpd) ??
        extractNumericMetric(device?.vpd) ??
        extractNumericMetric(latestRawPoint?.vpd) ??
        null
      : null,
  };
  const relayStatuses = normalizeRelayMap(latestReading || device || {});
  const relayModes = normalizeRelayModes(latestReading || device || {});
  const canShowDeviceId = fullDeviceId && fullDeviceId !== '--';

  const leftCharts = [chartSpecs[0], chartSpecs[2]];
  const rightCharts = vpdAnalysisAllowed ? [chartSpecs[1], chartSpecs[3]] : [chartSpecs[1]];

  useEffect(() => {
    if (!deviceIdCopied) return undefined;
    const timerId = window.setTimeout(() => setDeviceIdCopied(false), 2000);
    return () => window.clearTimeout(timerId);
  }, [deviceIdCopied]);

  const handleDeviceIdClick = async () => {
    const nextExpanded = !deviceIdExpanded;
    setDeviceIdExpanded(nextExpanded);
    if (!nextExpanded) {
      setDeviceIdCopied(false);
      return;
    }
    try {
      const copied = await copyText(fullDeviceId);
      setDeviceIdCopied(Boolean(copied));
    } catch (_) {
      setDeviceIdCopied(false);
    }
  };

  return (
    <div className="card climate-card">
      <div className="card-body climate-card-body">
        <div className="climate-card-topbar">
          <h4 className="card-title climate-card-title mb-0">{deviceTitle}</h4>
          {canShowDeviceId ? (
            <button
              type="button"
              className={`climate-card-id-button ${deviceIdExpanded ? 'is-open' : ''}`}
              onClick={handleDeviceIdClick}
              title={fullDeviceId}
              aria-label={text.idPreviewAria}
            >
              <span className="climate-card-id-button__label">{t('deviceCard.idLabel')}</span>
              <span className="climate-card-id-button__value">{shortDeviceId}</span>
            </button>
          ) : null}
        </div>
        {canShowDeviceId && deviceIdExpanded ? (
          <div className="climate-card-id-panel" role="status" aria-live="polite">
            <div className="climate-card-id-panel__row">
              <span className="climate-card-id-panel__label">{t('deviceCard.idLabel')}</span>
              {deviceIdCopied ? (
                <span className="climate-card-id-panel__copied">{text.copied}</span>
              ) : null}
            </div>
            <div className="climate-card-id-full">{fullDeviceId}</div>
          </div>
        ) : null}
        <div className="climate-card-layout">
          <div className="climate-layout-left">
            <div className="climate-charts-column">
              {leftCharts.map((spec) => (
                <ClimateMetricChart
                  key={spec.key}
                  spec={spec}
                  points={points}
                  value={liveMetrics[spec.valueKey]}
                  text={text}
                  palette={palette}
                  nightMask={nightMask}
                  isMobileViewport={isMobileViewport}
                />
              ))}
            </div>
          </div>
          <div className="climate-layout-center">
            <div className="climate-control-column text-center">
              <div
                className="d-flex flex-column align-items-center w-100 min-w-0 climate-control-stack-inner"
                style={{ gap: '12px' }}
              >
                <div className="d-flex justify-content-center flex-shrink-0">
                  {renderAction(text.settings, 'btn btn-outline-secondary btn-sm', {
                    onClick: onSettings,
                    href: onSettings ? '' : settingsHref,
                    disabled: !onSettings && !settingsHref,
                  })}
                </div>
                <div className={`climate-connection-badge climate-connection-badge--${connectionState}`}>
                  <span aria-hidden="true">{statusBadgeEmoji}</span>
                  <span>{statusBadgeText}</span>
                </div>
                <div className="shefa-socket-grid" aria-label={text.relays}>
                  <RelayBadge label={text.relayAc} value={{ state: relayStatuses.ac, mode: relayModes.ac }} onLabel={text.on} offLabel={text.offline} stale={isStaleConnection} />
                  <RelayBadge label={text.relayDryer} value={{ state: relayStatuses.dryer, mode: relayModes.dryer }} onLabel={text.on} offLabel={text.offline} stale={isStaleConnection} />
                  <RelayBadge label={text.relayCo2} value={{ state: relayStatuses.co2, mode: relayModes.co2 }} onLabel={text.on} offLabel={text.offline} stale={isStaleConnection} />
                  <RelayBadge label={text.relayExtra} value={{ state: relayStatuses.extra, mode: relayModes.extra }} onLabel={text.on} offLabel={text.offline} stale={isStaleConnection} />
                </div>
                <div className="small text-muted">
                  {text.updated}: {headerTimestamp}
                </div>
                <div className="small text-muted">
                  {text.climateLightStatus}:{' '}
                  {formatLightState(normalizeLightBinary(liveMetrics.light), text.day, text.night)}
                </div>
              </div>
            </div>
          </div>
          <div className="climate-layout-right">
            <div className="climate-charts-column">
              {rightCharts.map((spec) => (
                <ClimateMetricChart
                  key={spec.key}
                  spec={spec}
                  points={points}
                  value={liveMetrics[spec.valueKey]}
                  text={text}
                  palette={palette}
                  nightMask={nightMask}
                  isMobileViewport={isMobileViewport}
                />
              ))}
              {!vpdAnalysisAllowed ? (
                <div className="climate-chart-block climate-chart-block--plan-locked">
                  <div className="climate-chart-header">
                    <h6 className="climate-chart-title mb-0 text-muted">
                      <i className="fas fa-lock me-2" aria-hidden />
                      {t('vpd')}
                    </h6>
                  </div>
                  <p className="small text-muted mb-0">{t('climateVpdProOnly')}</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {!points.length && !loading ? (
          <div className="text-center text-muted mt-3">{text.noData24h}</div>
        ) : null}
      </div>
    </div>
  );
}
